// Push do atendimento no app nativo (iOS e Android). A API manda pelo
// Firebase Cloud Messaging, o mesmo caminho do Curral Assist: o aparelho
// registra o token FCM em PUT /manager/support/push-token e informa qual
// conversa está aberta (push-presence) para não receber push dela.
// No navegador tudo aqui é no-op.

import { App } from '@capacitor/app';
import { FirebaseMessaging, Importance } from '@capacitor-firebase/messaging';
import { axiosClient } from '@/api/axiosClient';
import { ehNativo, plataforma } from './nativo';

// Mesmo canal que a API usa no payload Android (supportPushService.js).
const CANAL_ANDROID = 'support_updates';
// A API ignora a presença depois de 90 s sem atualizar; renova antes disso.
const RENOVAR_PRESENCA_MS = 60_000;

let tokenAtual: string | null = null;
let iniciado = false;
let conversaAtiva: number | null = null;
let appAtivo = true;
let timerPresenca: ReturnType<typeof setInterval> | null = null;

async function registrarToken(token: string) {
  tokenAtual = token;
  await axiosClient.put('/manager/support/push-token', { token, platform: plataforma });
}

async function enviarPresenca() {
  if (!tokenAtual) return;
  try {
    await axiosClient.put('/manager/support/push-presence', {
      token: tokenAtual,
      conversation_id: appAtivo ? conversaAtiva : null,
    });
  } catch {
    // sem rede: na pior das hipóteses chega um push da conversa aberta
  }
}

function reagendarPresenca() {
  if (timerPresenca) clearInterval(timerPresenca);
  timerPresenca = null;
  void enviarPresenca();
  if (appAtivo && conversaAtiva != null) {
    timerPresenca = setInterval(() => void enviarPresenca(), RENOVAR_PRESENCA_MS);
  }
}

// Abre a conversa do push sem recarregar o app (o React Router escuta popstate).
function abrirConversa(conversationId: string | undefined) {
  const destino = conversationId
    ? `/suporte?conv=${encodeURIComponent(conversationId)}`
    : '/suporte';
  window.history.pushState({}, '', destino);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// Chamado depois do login, para quem atende o suporte.
export async function iniciarPushNativo() {
  if (!ehNativo || iniciado) return;
  iniciado = true;
  try {
    let { receive } = await FirebaseMessaging.checkPermissions();
    if (receive === 'prompt' || receive === 'prompt-with-rationale') {
      ({ receive } = await FirebaseMessaging.requestPermissions());
    }
    if (receive !== 'granted') return;

    if (plataforma === 'android') {
      await FirebaseMessaging.createChannel({
        id: CANAL_ANDROID,
        name: 'Atendimento',
        description: 'Mensagens novas e transferências na Central de Atendimento',
        importance: Importance.High,
        sound: 'default',
        vibration: true,
      });
    }

    await FirebaseMessaging.addListener('tokenReceived', ({ token }) => {
      void registrarToken(token).catch(() => {});
    });
    await FirebaseMessaging.addListener('notificationActionPerformed', ({ notification }) => {
      const dados = notification.data as { conversationId?: string } | undefined;
      abrirConversa(dados?.conversationId);
    });
    await App.addListener('appStateChange', ({ isActive }) => {
      appAtivo = isActive;
      reagendarPresenca();
    });

    const { token } = await FirebaseMessaging.getToken();
    await registrarToken(token);
    reagendarPresenca();
  } catch (error) {
    console.error('Push nativo indisponível:', error);
  }
}

// A Central chama ao abrir e fechar uma conversa.
export function informarConversaAtiva(conversationId: number | null) {
  if (!ehNativo || conversaAtiva === conversationId) return;
  conversaAtiva = conversationId;
  reagendarPresenca();
}

// Logout: o aparelho para de receber push deste login.
export async function encerrarPushNativo() {
  if (!ehNativo || !tokenAtual) return;
  if (timerPresenca) clearInterval(timerPresenca);
  timerPresenca = null;
  try {
    await axiosClient.delete('/manager/support/push-token', { data: { token: tokenAtual } });
  } catch {
    // a API também tira os aparelhos da sessão no logout
  }
}
